import clsx from "clsx";
import gsap from 'gsap';
import { forwardRef } from 'react';

/**
 * Calculates distance from a point to a center point.
 * @param {{ x: number, y: number }} point
 * @param {{ x: number, y: number }} center
 * @returns {number}
 */
const getDistanceFromCenter = (point, center) => {
  return Math.sqrt(Math.pow(point?.x - center.x, 2) + Math.pow(point?.y - center.y, 2));
};

/**
 * Sets initial hidden state for pin elements.
 * @param {HTMLElement[]} pins - Array of pin elements
 */
export const setPinsHidden = (pins) => {
  pins.forEach((pin) => {
    const marker = pin.querySelector('.pin-marker');
    const shadow = pin.querySelector('.pin-shadow');

    gsap.set(marker, { y: -60, opacity: 0 });
    gsap.set(shadow, { scale: 0.3, opacity: 0, transformOrigin: 'center center' });
  });
};

/**
 * Animates pins dropping in with bounce effect.
 * @param {HTMLElement[]} pins - Array of pin elements
 * @param {Object} [options]
 * @param {number} [options.baseDelay=0.2] - Initial delay before first pin drops
 * @param {number} [options.staggerDelay=0.1] - Delay between each pin
 * @param {'start'|'center'} [options.staggerFrom='start'] - Stagger order: 'start' for array order, 'center' for distance from center
 * @param {{ x: number, y: number }[]} [options.positions] - Pin positions (required for 'center' stagger)
 * @param {{ x: number, y: number }} [options.center] - Center point for 'center' stagger ordering
 */
export const animatePinsDrop = (pins, options = {}) => {
  const {
    baseDelay = 0.2,
    staggerDelay = 0.1,
    staggerFrom = 'start',
    positions,
    center = { x: 50, y: 50 }
  } = options;

  // Sort pins by distance from center if staggerFrom is 'center'
  let orderedPins = pins;
  if (staggerFrom === 'center' && positions && positions.length === pins.length) {
    const pinsWithDistance = pins.map((pin, index) => ({
      pin,
      distance: getDistanceFromCenter(positions[index], center)
    }));
    pinsWithDistance.sort((a, b) => a.distance - b.distance);
    orderedPins = pinsWithDistance.map(({ pin }) => pin);
  }

  orderedPins.forEach((pin, index) => {
    const marker = pin.querySelector('.pin-marker');
    const shadow = pin.querySelector('.pin-shadow');
    const delay = baseDelay + index * staggerDelay;

    // Animate marker fading in
    gsap.to(marker, {
      opacity: 1,
      duration: 0.3,
      ease: 'power2.out',
      delay,
    });

    // Animate marker dropping
    gsap.to(marker, {
      y: 0,
      duration: 0.4,
      ease: 'power2.out',
      delay,
    });

    // Animate shadow growing
    gsap.to(shadow, {
      scale: 1,
      opacity: 1,
      duration: 0.4,
      ease: 'power2.out',
      delay,
    });
  });
};

/**
 * Map pin component. Interactive when label is provided, decorative otherwise.
 * @param {Object} props
 * @param {string} [props.className] - Additional CSS classes
 * @param {number} props.top - Top position as percentage
 * @param {number} props.left - Left position as percentage
 * @param {string} [props.label] - Accessible label for the pin location. If omitted, pin is decorative.
 * @param {number} [props.scale=1] - Scale factor for counter-scaling during zoom
 * @param {string} [props.transition='none'] - CSS transition value
 * @param {boolean} [props.large=false] - Whether to use larger size on desktop
 * @param {boolean} [props.initiallyHidden=false] - Whether to start hidden for drop animation
 * @param {Function} [props.onClick] - Click handler
 * @param {boolean} [props.isSelected=false] - Whether this pin's popover is open
 * @param {string} [props.popoverId] - ID of the controlled popover element
 */
const Pin = forwardRef(function Pin({ className = '', top, left, label, scale = 1, transition = 'none', large = false, initiallyHidden = false, onClick, onFocus, onMouseEnter, onMouseLeave, isSelected = false, popoverId }, ref) {
  const isInteractive = Boolean(label || onClick);

  const commonProps = {
    ref,
    className: clsx(
      'w-8 h-auto overflow-visible text-[#07536E] hover:text-[#1480A7]',
      large && 'lg:w-10',
      isSelected && '!text-[#1480A7]',
      isInteractive && 'cursor-pointer',
      className
    ),
    style: { top: `${top}%`, left: `${left}%`, transform: `translate(-50%, -83%) scale(${scale})`, transformOrigin: '50% 83%', transition: `${transition}, color 0.2s` },
  };

  const svgContent = (
    <svg
      className="w-full h-auto overflow-visible"
      width="43"
      height="61"
      viewBox="0 0 43 61"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <g className="pin-shadow" opacity="0.6" filter="url(#filter0_f_10227_14048)" style={initiallyHidden ? { opacity: 0 } : undefined}>
        <ellipse cx="21.25" cy="52.5" rx="15" ry="1.875" fill="black"/>
      </g>
      <g className="pin-marker" style={initiallyHidden ? { opacity: 0 } : undefined}>
        <path d="M41.25 19.375C41.25 35 21.25 50.625 21.25 50.625C21.25 50.625 1.25 35 1.25 19.375C1.25 9.01967 10.2043 0.625 21.25 0.625C32.2957 0.625 41.25 9.01967 41.25 19.375Z" fill="currentColor"/>
        <path d="M41.25 19.375C41.25 35 21.25 50.625 21.25 50.625C21.25 50.625 1.25 35 1.25 19.375C1.25 9.01967 10.2043 0.625 21.25 0.625C32.2957 0.625 41.25 9.01967 41.25 19.375Z" fill="none" stroke="currentColor" strokeWidth="1.25"/>
        <path d="M41.25 19.375C41.25 35 21.25 50.625 21.25 50.625C21.25 50.625 1.25 35 1.25 19.375C1.25 9.01967 10.2043 0.625 21.25 0.625C32.2957 0.625 41.25 9.01967 41.25 19.375Z" fill="none" stroke="white" strokeOpacity="0.3" strokeWidth="1.25"/>
        <circle cx="21.25" cy="20.625" r="6.25" fill="#F5F0E3"/>
      </g>
      <defs>
        <filter id="filter0_f_10227_14048" x="0" y="44.375" width="42.5" height="16.25" filterUnits="userSpaceOnUse" colorInterpolationFilters="sRGB">
          <feFlood floodOpacity="0" result="BackgroundImageFix"/>
          <feBlend mode="normal" in="SourceGraphic" in2="BackgroundImageFix" result="shape"/>
          <feGaussianBlur stdDeviation="3.125" result="effect1_foregroundBlur_10227_14048"/>
        </filter>
      </defs>
    </svg>
  );

  if (isInteractive) {
    return (
      <button
        {...commonProps}
        type="button"
        aria-label={label ? `View story from ${label}` : undefined}
        aria-expanded={isSelected}
        aria-controls={isSelected ? popoverId : undefined}
        onClick={onClick}
        onTouchStart={(e) => e.stopPropagation()}
        onTouchEnd={(e) => e.stopPropagation()}
        onFocus={onFocus}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
      >
        {svgContent}
      </button>
    );
  }

  return (
    <span {...commonProps} aria-hidden="true">
      {svgContent}
    </span>
  );
});

export default Pin;
